import { useEffect } from "react";

import { AstridDaoStoreState } from "@astrid-dao/lib-base";
import { Decimal18 } from "@astrid-dao/lib-base";
import { EthersTransactionOverrides } from "@astrid-dao/lib-ethers";
import { promiseAllValues } from "@astrid-dao/lib-ethers/dist/src/_utils";
import { Contract, Signer } from "ethers";
import useSWR from "swr";

import { BaseProp } from "../../../@types/base-types";
import { laggy } from "../../../data/swr-middleware";
import { AstridDaoStoreUpdate, useAstridDaoReducer } from "../../../hooks/useAstridDaoReducer";
import { useAstridDao } from "../../../providers/AstridDaoProvider";
import { assertContractExist, collateralTokenMap } from "../../../rpc-contract-service/contracts";
import ContractsJson from "../../../rpc-contract-service/contracts.json";
import { decimalify } from "../../../rpc-contract-service/utils/utils";
import { useMyTransactionState } from "../../Transaction";
import { ConvertWastrViewAction, ConvertWastrViewContext } from "./ConvertWastrViewContext";

type ConvertWastrViewProviderAction =
  | AstridDaoStoreUpdate
  | ConvertWastrViewAction
  | { type: "startChange" | "abortChange" };

type ConvertWastrViewProviderState = {
  changePending: boolean;
  depositing: boolean;
};

const init = (storeState: AstridDaoStoreState): ConvertWastrViewProviderState => ({
  changePending: false,
  depositing: true
});

const reduce = (
  state: ConvertWastrViewProviderState,
  action: ConvertWastrViewProviderAction
): ConvertWastrViewProviderState => {
  switch (action.type) {
    case "startDepositing":
      return { ...state, depositing: true };

    case "startWithdrawing":
      return { ...state, depositing: false };

    case "startChange":
      return { ...state, changePending: true };

    case "abortChange":
      return { ...state, changePending: false };
  }

  return state;
};

async function getConvertWastrData({
  userAddress,
  borrowerOperationsAddress,
  wastrContract
}: {
  userAddress: string;
  borrowerOperationsAddress: string;
  wastrContract: Contract;
}) {
  const { wastrBalance, wastrAllowance } = await promiseAllValues({
    wastrBalance: getWastrBalance(userAddress, wastrContract),
    wastrAllowance: getWastrAllowance({
      userAddress,
      borrowerOperationsAddress,
      wastrContract
    })
  });

  return {
    wastrBalance,
    wastrAllowance
  };
}

export const ConvertWastrViewProvider: React.FC<BaseProp> = ({ children }) => {
  const stakingTransactionState = useMyTransactionState("stake");
  const [{ depositing, changePending }, dispatch] = useAstridDaoReducer(reduce, init);
  const { account, isTestnet, signer } = useAstridDao();

  useEffect(() => {
    if (
      stakingTransactionState.type === "waitingForApproval" ||
      stakingTransactionState.type === "waitingForConfirmation"
    ) {
      dispatch({ type: "startChange" });
    } else if (
      stakingTransactionState.type === "failed" ||
      stakingTransactionState.type === "cancelled"
    ) {
      dispatch({ type: "abortChange" });
    }
  }, [stakingTransactionState.type, dispatch]);

  const contractToUse = isTestnet ? "shibuya" : "astar";
  const wastrContracts = ContractsJson.network[contractToUse].collaterals.find(collateral => {
    return collateral.name === "WASTR";
  });
  const wastrBorrowOperationsContractAddress = wastrContracts?.contracts.borrowerOperations ?? "";
  const wastrTokenContract = assertContractExist(collateralTokenMap.get("WASTR"));

  const { data } = useSWR(
    [
      {
        userAddress: account,
        borrowerOperationsAddress: wastrBorrowOperationsContractAddress,
        wastrContract: wastrTokenContract
      }
    ],
    getConvertWastrData,
    {
      use: [laggy],
      fallbackData: undefined
    }
  );
  const hasApprovedTransactions = !data?.wastrAllowance.isZero;
  const approveWastr = createWastrApprover({
    signer,
    wastrContract: wastrTokenContract,
    borrowerOperationsAddress: wastrBorrowOperationsContractAddress
  });
  const depositAstr = createWastrDepositor({
    signer,
    wastrContract: wastrTokenContract
  });
  const withdrawWastr = createWastrWithdrawer({
    signer,
    wastrContract: wastrTokenContract
  });

  return (
    <ConvertWastrViewContext.Provider
      value={{
        view: hasApprovedTransactions ? (depositing ? "DEPOSITING" : "WITHDRAWING") : "NO_APPROVAL",
        changePending,
        dispatch,
        data,
        approveWastr,
        depositAstr,
        withdrawWastr
      }}
    >
      {children}
    </ConvertWastrViewContext.Provider>
  );
};

function getWastrBalance(userAddress: string, wastrContract: Contract): Promise<Decimal18> {
  return wastrContract.balanceOf(userAddress).then(decimalify);
}

function getWastrAllowance({
  userAddress,
  borrowerOperationsAddress,
  wastrContract
}: {
  userAddress: string;
  borrowerOperationsAddress: string;
  wastrContract: Contract;
}): Promise<Decimal18> {
  return wastrContract.allowance(userAddress, borrowerOperationsAddress).then(decimalify);
}

function createWastrApprover({
  signer,
  wastrContract,
  borrowerOperationsAddress
}: {
  signer: Signer;
  wastrContract: Contract;
  borrowerOperationsAddress: string;
}) {
  const connectedWastrContract = wastrContract.connect(signer);

  return (amount: Decimal18) => {
    return connectedWastrContract.approve(borrowerOperationsAddress, amount.hex);
  };
}

function createWastrWithdrawer({
  signer,
  wastrContract
}: {
  signer: Signer;
  wastrContract: Contract;
}) {
  const connectedWastrContract = wastrContract.connect(signer);

  return async (amount: Decimal18, overrides?: EthersTransactionOverrides) => {
    const gasEstimate = await connectedWastrContract.estimateGas.withdraw(amount.hex);
    return connectedWastrContract.withdraw(amount.hex, {
      gasLimit: gasEstimate.mul(120).div(100),
      ...overrides
    });
  };
}

function createWastrDepositor({
  signer,
  wastrContract
}: {
  signer: Signer;
  wastrContract: Contract;
}) {
  const connectedWastrContract = wastrContract.connect(signer);

  return async (amount: Decimal18, overrides?: EthersTransactionOverrides) => {
    const gasEstimate = await connectedWastrContract.estimateGas.deposit({
      value: amount.hex
    });
    return connectedWastrContract.deposit({
      value: amount.hex,
      gasLimit: gasEstimate.mul(120).div(100),
      ...overrides
    });
  };
}
